/**
 * Refresh failures come in two kinds and the session must treat them differently
 * (OnEarly `app`, src/utils/authentication/errors.ts):
 *
 * - terminal — Ory rejected the refresh token (`invalid_grant` after rotation/reuse
 *   detection, a revoked client…). The session is over; the UI must re-authenticate.
 * - transient — network, 5xx, a malformed body. The session is still valid; retry later.
 *
 * Anything we can't classify is treated as transient on purpose: a flaky network must
 * never sign someone out.
 */
export class RefreshTokenRejectedError extends Error {
  constructor(
    readonly status: number | undefined,
    readonly code: string | undefined,
  ) {
    super(`Refresh token rejected${code ? ` (${code})` : ''}`);
    this.name = 'RefreshTokenRejectedError';
  }
}

export class RefreshUnavailableError extends Error {
  constructor(
    readonly status: number | undefined,
    override readonly cause?: unknown,
  ) {
    super('Token refresh unavailable');
    this.name = 'RefreshUnavailableError';
  }
}

/** OAuth2 error codes (RFC 6749 §5.2) that mean the grant itself is dead. */
export const TERMINAL_OAUTH_ERROR_CODES: ReadonlySet<string> = new Set([
  'invalid_grant',
  'invalid_client',
  'invalid_request',
  'unauthorized_client',
  'unsupported_grant_type',
]);

export function isTerminalRefreshFailure(status: number | undefined, body: unknown): boolean {
  const code =
    typeof body === 'object' && body !== null && 'error' in body
      ? (body as { error?: unknown }).error
      : undefined;
  if (typeof code === 'string' && TERMINAL_OAUTH_ERROR_CODES.has(code)) return true;
  return status === 400 || status === 401;
}

/**
 * Core answers errors as `{ error: true, message, code }`. Screens map the codes people
 * can act on (`INVALID_CODE`, `FLOW_EXPIRED`, `SOCIAL_LINK_UNSUPPORTED`…) to translated
 * copy and fall back to a generic message — internal strings never reach the UI.
 */
export function readCoreErrorCode(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null || !('body' in error)) return undefined;
  const body = (error as { body?: unknown }).body;
  if (typeof body !== 'object' || body === null) return undefined;
  const code = (body as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}
